"use client";

import Link from "next/link";
import Image from "next/image";
import ProductPlaceholder from "./ProductPlaceholder";
import { useCart } from "../CartContext";
import { STORE_ENABLED } from "@/lib/store-config";

export interface PairProduct {
  code: string;       // BP · 03
  sku: string;
  name: string;
  shortName: string;
  step: number;
  stepLabel: string;
  href: string;
  priceEUR: number;
  priceDisplay: string;
  image?: string;
}

interface PairsWithProps {
  currentCode: string;       // e.g. "BP · 23"
  currentStep: number;       // e.g. 3
  pairs: PairProduct[];
  totalDisplay?: string;     // e.g. "€194 (was €220)" — bundle total
  i18n?: {
    sectionLabel?: string;             // "WITHIN THE 5-STEP SYSTEM"
    headlineLead?: string;             // "{code} is step {n}."
    headlineTail?: string;             // "These are the other four."
    body?: string;                     // intro paragraph
    bundleEyebrow?: string;            // "BUNDLE — THE FULL ROUTINE"
    bundleHeading?: string;            // "Five steps · one routine · −12%"
    addLabel?: string;                 // "+ ADD"
    addBundleLabel?: string;           // "ADD BUNDLE →"
  };
}

export default function PairsWith({ currentCode, currentStep, pairs, totalDisplay, i18n }: PairsWithProps) {
  const t = {
    sectionLabel: i18n?.sectionLabel ?? "WITHIN THE 5-STEP SYSTEM",
    headlineLead: i18n?.headlineLead ?? `${currentCode} is step ${String(currentStep).padStart(2, "0")}.`,
    headlineTail: i18n?.headlineTail ?? "These are the other four.",
    body: i18n?.body ?? "Our five-step system — cleanse, tone, focus, boost, protect — delivers each active where it can work. Build the full routine and save 12%.",
    bundleEyebrow: i18n?.bundleEyebrow ?? "BUNDLE — THE FULL ROUTINE",
    bundleHeading: i18n?.bundleHeading ?? "Five steps · one routine · −12%",
    addLabel: i18n?.addLabel ?? "+ ADD",
    addBundleLabel: i18n?.addBundleLabel ?? "ADD BUNDLE →",
  };
  const { addItem, openCart } = useCart();

  const addOne = (p: PairProduct) => {
    addItem({
      sku: p.sku,
      name: p.name,
      shortName: p.shortName,
      priceEUR: p.priceEUR,
      image: p.image,
    });
  };

  const addBundle = () => {
    pairs.forEach(addOne);
    openCart();
  };

  return (
    <section
      style={{
        padding: "96px var(--bp-edge)",
        borderBottom: "1px solid var(--bp-rule)",
      }}
    >
      <div className="bp-section-head">
        <div>
          <div className="bp-mono"><span className="bp-bracket">{t.sectionLabel}</span></div>
          <h2
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              fontSize: "clamp(36px, 4.6vw, 64px)",
              lineHeight: 1.02,
              margin: "20px 0 0",
              textWrap: "balance" as never,
            }}
          >
            {t.headlineLead}
            <br />
            <em style={{ fontStyle: "italic", color: "var(--bp-ink-2)" }}>
              {t.headlineTail}
            </em>
          </h2>
        </div>
        <p style={{ maxWidth: 420, color: "var(--bp-ink-2)", margin: 0 }}>
          {t.body}
        </p>
      </div>

      <div
        className="bp-pairs-grid"
        style={{
          marginTop: 48,
          borderTop: "1px solid var(--bp-rule)",
          borderLeft: "1px solid var(--bp-rule)",
        }}
      >
        {pairs.map((p) => (
          <div
            key={p.sku}
            style={{
              borderBottom: "1px solid var(--bp-rule)",
              borderRight: "1px solid var(--bp-rule)",
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="bp-mono-sm">{p.stepLabel.toUpperCase()}</span>
              <span className="bp-mono-sm">{p.code}</span>
            </div>
            <Link
              href={p.href}
              style={{
                background: "var(--bp-bg-2)",
                aspectRatio: "1/1",
                position: "relative",
                overflow: "hidden",
                display: "block",
              }}
            >
              {p.image ? (
                <Image src={p.image} alt={p.name} fill sizes="(max-width:900px) 50vw, 25vw" style={{ objectFit: "cover" }} />
              ) : (
                <ProductPlaceholder label={p.code} />
              )}
            </Link>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "baseline" }}>
              <Link href={p.href} style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "var(--bp-serif)",
                    fontSize: 20,
                    fontWeight: 300,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.15,
                    textWrap: "balance" as never,
                    color: "var(--bp-ink)",
                  }}
                >
                  {p.name}
                </div>
                <div style={{ color: "var(--bp-ink-3)", fontSize: 13, marginTop: 4 }}>
                  Step {String(p.step).padStart(2, "0")} · {p.stepLabel}
                </div>
              </Link>
              {STORE_ENABLED && (
                <div style={{ fontFamily: "var(--bp-mono)", fontSize: 13, whiteSpace: "nowrap" }}>
                  {p.priceDisplay}
                </div>
              )}
            </div>
            {STORE_ENABLED && (
              <button onClick={() => { addOne(p); openCart(); }} className="bp-btn-ghost" style={{ marginTop: 4 }}>
                {t.addLabel}
              </button>
            )}
          </div>
        ))}
      </div>

      {STORE_ENABLED && (
        <div
          style={{
            marginTop: 24,
            padding: "22px 24px",
            background: "var(--bp-ink)",
            color: "var(--bp-bg)",
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            gap: 24,
            alignItems: "center",
          }}
          className="bp-bundle-strip"
        >
          <div>
            <div style={{ fontFamily: "var(--bp-mono)", fontSize: 11, letterSpacing: "0.14em", color: "oklch(0.72 0 0)" }}>
              {t.bundleEyebrow}
            </div>
            <div
              style={{
                fontFamily: "var(--bp-serif)",
                fontSize: 26,
                fontWeight: 300,
                marginTop: 4,
                letterSpacing: "-0.01em",
              }}
            >
              {t.bundleHeading}
            </div>
          </div>
          {totalDisplay && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "var(--bp-mono)", fontSize: 18 }}>{totalDisplay}</div>
            </div>
          )}
          <button
            onClick={addBundle}
            className="bp-btn-ghost"
            style={{ borderColor: "var(--bp-bg)", color: "var(--bp-bg)" }}
          >
            {t.addBundleLabel}
          </button>
        </div>
      )}

      <style>{`
        .bp-pairs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        @media (max-width: 900px) {
          .bp-pairs-grid { grid-template-columns: repeat(2, 1fr); }
          .bp-bundle-strip {
            grid-template-columns: 1fr !important;
            text-align: left;
          }
        }
        @media (max-width: 560px) {
          .bp-pairs-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}
